import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  Store, 
  MapPin, 
  Globe,
  Percent,
  CreditCard
} from "lucide-react";

const RetailersPage = () => {
  const { user } = useAuth();
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRetailers();
  }, []);

  const fetchRetailers = async () => {
    try {
      const response = await axios.get(`${API}/retailers`);
      setRetailers(response.data);
    } catch (error) {
      toast.error("Failed to load retailers");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Partner Retailers" subtitle="Show your eCard to get exclusive discounts">
      {/* Discount Info Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="gradient-ocean border-0 mb-8">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Your Member Discount</h3>
                <p className="text-white/70">Show your eCard to any partner retailer</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <span className="text-5xl font-extrabold text-[var(--sunrise-orange)]">10%</span>
              <p className="text-white/70 text-sm">OFF all purchases</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* How to Use */}
      <div className="mb-8 p-6 bg-[var(--sunrise-orange)]/10 rounded-2xl">
        <h3 className="font-bold text-[var(--deep-ocean)] mb-3">How to Get Your Discount</h3>
        <ol className="space-y-2 text-[var(--river-stone)]">
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-[var(--deep-ocean)] text-white text-sm flex items-center justify-center flex-shrink-0">1</span>
            <span>Visit any partner retailer below</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-[var(--deep-ocean)] text-white text-sm flex items-center justify-center flex-shrink-0">2</span>
            <span>Go to your Dashboard and tap your membership card to flip it</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-[var(--deep-ocean)] text-white text-sm flex items-center justify-center flex-shrink-0">3</span>
            <span>Show the QR code and member number to the cashier</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-[var(--deep-ocean)] text-white text-sm flex items-center justify-center flex-shrink-0">4</span>
            <span>Enjoy your 10% discount!</span>
          </li>
        </ol>
      </div>

      {/* Retailers List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {retailers.map((retailer, index) => (
            <motion.div
              key={retailer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="retailer-coupon bg-white p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-[var(--deep-ocean)]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {retailer.logo_url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${retailer.logo_url}`}
                        alt={retailer.name}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <Store className="w-8 h-8 text-[var(--deep-ocean)]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-[var(--deep-ocean)]">{retailer.name}</h3>
                      <Badge className="bg-[var(--success)] text-white">
                        <Percent className="w-3 h-3 mr-1" />
                        {retailer.discount_percent}% OFF
                      </Badge>
                    </div>
                    {retailer.description && (
                      <p className="text-sm text-[var(--river-stone)] mb-3">{retailer.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-[var(--river-stone)]">
                      {retailer.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{retailer.location}</span>
                        </div>
                      )}
                      {retailer.website && (
                        <a 
                          href={retailer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[var(--deep-ocean)] hover:underline"
                        >
                          <Globe className="w-4 h-4" />
                          <span>Website</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {!loading && retailers.length === 0 && (
        <Card className="bg-white card-shadow border-0">
          <CardContent className="p-12 text-center">
            <Store className="w-16 h-16 text-[var(--river-stone)] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[var(--deep-ocean)] mb-2">No retailers yet</h3>
            <p className="text-[var(--river-stone)]">
              Partner retailers will be added soon!
            </p>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};

export default RetailersPage;
